import React, { useMemo, useState } from 'react';
import { v4 } from 'uuid';
import Icon, { IconType } from '../../icons/Icon';
import { Label } from '../Label';
import { LabelPlace } from '../Label/Label';

interface CheckboxProps {
  label?: string;

  value: boolean;
  onChange: (value: boolean) => void;

  disabled?: boolean;

  tooltip?: React.ReactNode;
}

export default function Checkbox({ label, value, onChange, disabled = false, tooltip }: CheckboxProps) {
  const [id] = useState(v4());

  const borderClass = useMemo(() => {
    if (disabled) return 'border border-solid border-gray-disabled';
    if (!value) return 'border border-solid border-gray-default';

    return 'border border-solid border-primary-800';
  }, [value, disabled]);

  const backgroundColorClass = useMemo(() => {
    if (disabled) return 'bg-gray-disabled';
    if (value) return 'bg-primary-100 hover:bg-primary-200';

    return '';
  }, [value, disabled]);

  const strokeColorClass = useMemo(() => {
    if (disabled) return 'stroke-gray-400';
    if (value) return 'stroke-primary-800';

    return '';
  }, [value, disabled]);

  const checkBox = (
    <div className={`relative flex h-fit w-fit cursor-pointer items-center !justify-center focus-within:ring-2`}>
      {value && (
        <Icon
          stroke={strokeColorClass}
          type={IconType.check}
          size={'h-3.5 w-3.5'}
          className='pointer-events-none absolute'
        />
      )}
      <input
        className={`unset h-4 w-4 rounded-1 ${borderClass} ${backgroundColorClass}`}
        type='checkbox'
        name={label}
        id={id}
        checked={value}
        disabled={disabled}
        onKeyDown={event => event.stopPropagation()}
        onChange={event => onChange(event.currentTarget.checked)}
      />
    </div>
  );

  if (!label) return checkBox;

  return (
    <Label
      tooltip={tooltip}
      label={label}
      inputId={id}
      place={LabelPlace.right}
      className='w-fit cursor-pointer select-none'
      gap={'gap-2'}
    >
      {checkBox}
    </Label>
  );
}
